// theming/ThemeCard.jsx
import React, { useMemo } from 'react';
import { Moon, Star, Sun } from 'lucide-react';
import clsx from 'clsx';
import { useFontLoader } from '@/dashboard/hooks/useFontLoader';
import { getThemeCardVisuals } from './getThemeCardVisuals';
import { ThemeCardMenu } from './ThemeCardMenu';

function ModeIndicator({ supportsLight, supportsDark }) {
  if (!supportsLight && !supportsDark) return null;
  return (
    <div className="ml-auto flex items-center gap-1 text-muted-foreground">
      {supportsLight && <Sun className="h-3.5 w-3.5" aria-hidden="true" />}
      {supportsDark && <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
    </div>
  );
}

export function ThemeCard({
  theme,
  active = false,
  isDefault = false,
  canDelete = false,
  colorMode,
  onSelect,
  onSetDefault,
  onDuplicate,
  onDelete,
  hideActions = false,
}) {
  const visuals = useMemo(() => getThemeCardVisuals(theme, colorMode), [theme, colorMode]);
  const { isLoaded } = useFontLoader(visuals.fontFamily || null, !!visuals.fontFamily);
  const letterFontFamily =
    visuals.fontFamily && isLoaded ? `"${visuals.fontFamily}", sans-serif` : 'sans-serif';

  const swatch = color => (
    <span
      className="h-3.5 w-3.5 rounded-[3px]"
      style={{
        backgroundColor: color,
        boxShadow: 'inset 0 0 0 1px rgb(from currentColor r g b / 0.15)',
      }}
    />
  );

  return (
    <div
      data-testid="theme-card"
      className={clsx(
        'group relative flex flex-col overflow-hidden rounded-[10px] bg-white dark:bg-background text-left border transition-colors',
        'has-[[data-theme-select]:focus-visible]:ring-2 has-[[data-theme-select]:focus-visible]:ring-ring has-[[data-theme-select]:focus-visible]:ring-offset-2',
        active ? 'border-primary' : 'border-input',
      )}
    >
      {/* A sibling of the menu button: a button inside a button hides the menu from assistive tech. */}
      <button
        type="button"
        data-theme-select
        aria-pressed={active}
        aria-label={theme.name}
        onClick={() => onSelect?.(theme)}
        className="absolute inset-0 z-[1] cursor-pointer outline-none"
      />

      {/* Preview area — uses the theme's surface + text colors */}
      <div
        className="relative h-[86px] border-b border-gray-200 dark:border-border p-3"
        style={{ backgroundColor: visuals.surface, color: visuals.text }}
      >
        <span
          aria-hidden
          style={{ backgroundColor: visuals.hoverOverlay }}
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
        />

        <div
          className="relative text-2xl leading-none"
          style={{
            fontFamily: letterFontFamily,
            color: visuals.letterColor,
            fontWeight: visuals.fontWeight,
          }}
        >
          {visuals.initial}
        </div>

        <div className="absolute bottom-3 left-3 flex items-center gap-1">
          {swatch(visuals.accent)}
          {swatch(visuals.text)}
        </div>

        {isDefault && (
          <span aria-label="Default theme" className="absolute bottom-3 right-3">
            <Star className="h-4 w-4 fill-current" />
          </span>
        )}

        {!hideActions && (
          <div className="absolute right-1.5 top-1.5 z-[2]">
            <ThemeCardMenu
              isDefault={isDefault}
              canDelete={canDelete}
              iconColor={visuals.textMuted}
              onSetDefault={() => onSetDefault?.(theme)}
              onDuplicate={() => onDuplicate?.(theme)}
              onDelete={() => onDelete?.(theme)}
            />
          </div>
        )}
      </div>

      {/* Spec strip — neutral, always white */}
      <div className="relative flex items-center gap-2 px-3 py-2 transition-colors group-hover:bg-accent/50">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-tight">{theme.name}</div>
          <div className="truncate text-xs text-muted-foreground">{visuals.fontLabel}</div>
        </div>
        <ModeIndicator supportsLight={visuals.supportsLight} supportsDark={visuals.supportsDark} />
      </div>
    </div>
  );
}

export default ThemeCard;
